/**
 * Loads the curated set of selectable languages for intake template variants.
 *
 * Languages come from the FHIR `urn:ietf:bcp:47` ValueSet
 * (`http://hl7.org/fhir/ValueSet/languages`) via Medplum's `valueSetExpand`
 * operation. Results are cached per session in module-level state since the
 * list is large and effectively static. When the expansion fails (offline,
 * permissions, etc.) we fall back to a curated short list of the languages
 * we routinely see in our pilot deployments.
 *
 * Returned options are sorted by display label so admins can scan / search
 * them in the variant editor's `Select`.
 */
import { useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { ValueSet, ValueSetExpansionContains } from '@medplum/fhirtypes';


/** A language choice surfaced to admins. `code` is BCP-47 (`en`, `es`, `zh-Hans`, …). */
export interface LanguageOption {
  code: string;
  /** Native + English label, e.g. `Spanish (Español)`. Falls back to code. */
  display: string;
}

const LANGUAGES_VALUESET_URL = 'http://hl7.org/fhir/ValueSet/languages';

/**
 * Fallback short-list. Kept intentionally small — admins can still publish a
 * language from the live ValueSet expansion when online; this list only
 * exists so the editor remains usable offline / in mock mode.
 */
export const FALLBACK_LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: 'en', display: 'English' },
  { code: 'es', display: 'Spanish (Español)' },
  { code: 'zh-Hans', display: 'Chinese, Simplified (简体中文)' },
  { code: 'zh-Hant', display: 'Chinese, Traditional (繁體中文)' },
  { code: 'vi', display: 'Vietnamese (Tiếng Việt)' },
  { code: 'tl', display: 'Tagalog' },
  { code: 'ar', display: 'Arabic (العربية)' },
  { code: 'ru', display: 'Russian (Русский)' },
  { code: 'fr', display: 'French (Français)' },
  { code: 'pt', display: 'Portuguese (Português)' },
  { code: 'ko', display: 'Korean (한국어)' },
  { code: 'hi', display: 'Hindi (हिन्दी)' },
  { code: 'fa', display: 'Persian (فارسی)' },
];

let cachedOptions: LanguageOption[] | null = null;

interface UseLanguageOptionsResult {
  options: LanguageOption[];
  loading: boolean;
  error: Error | null;
}

function dedupeAndSort(options: LanguageOption[]): LanguageOption[] {
  const map = new Map<string, LanguageOption>();
  for (const o of options) {
    if (!o.code) continue;
    if (!map.has(o.code)) map.set(o.code, o);
  }
  return [...map.values()].sort((a, b) => a.display.localeCompare(b.display));
}

function expansionToOptions(expansion: ValueSet['expansion']): LanguageOption[] {
  const contains: ValueSetExpansionContains[] = expansion?.contains ?? [];
  return contains
    .map((c) => ({ code: c.code ?? '', display: c.display ?? c.code ?? '' }))
    .filter((o) => Boolean(o.code));
}

/**
 * Look up a language label by code, with fallback. Useful in row UIs that
 * show a previously-saved variant whose code may not be in the user's
 * filtered options list.
 */
export function getLanguageDisplay(
  code: string | null | undefined,
  options: LanguageOption[],
): string {
  if (!code) return 'Default';
  const match = options.find((o) => o.code === code);
  if (match) return match.display;
  const fallback = FALLBACK_LANGUAGE_OPTIONS.find((o) => o.code === code);
  return fallback?.display ?? code;
}

export function useLanguageOptions(): UseLanguageOptionsResult {
  const medplum = useMedplum();
  const [options, setOptions] = useState<LanguageOption[]>(
    cachedOptions ?? FALLBACK_LANGUAGE_OPTIONS,
  );
  const [loading, setLoading] = useState(!cachedOptions);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (cachedOptions) {
      setOptions(cachedOptions);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Use the loosely-typed primitive `get` to remain compatible across
        // Medplum SDK versions that may or may not expose a `valueSetExpand`
        // helper. Falls back to the curated short-list on any failure.
        const path = `fhir/R4/ValueSet/$expand?url=${encodeURIComponent(LANGUAGES_VALUESET_URL)}&count=1000`;
        const expanded = (await medplum.get(path)) as ValueSet;
        if (cancelled) return;
        const opts = dedupeAndSort([
          ...expansionToOptions(expanded?.expansion),
          ...FALLBACK_LANGUAGE_OPTIONS,
        ]);
        cachedOptions = opts;
        setOptions(opts);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to load languages'));
          setOptions(FALLBACK_LANGUAGE_OPTIONS);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [medplum]);

  return { options, loading, error };
}
